"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Menu, ShoppingBag, X } from "lucide-react";

import { useCart } from "@/context/cart-context";

type TProps = Readonly<{
  variant?: "dark" | "light";
}>;

const NAV_LINKS = [
  {
    href: "/dongl",
    label: "Dongl",
  },
  {
    href: "https://simpleble.org/?utm_source=simpleble_store&utm_medium=store&utm_campaign=store_navbar",
    label: "Main site",
  },
  {
    href: "https://docs.simpleble.org/?utm_source=simpleble_store&utm_medium=store&utm_campaign=store_navbar",
    label: "Docs",
  },
  { href: "https://github.com/simpleble/simpleble", label: "GitHub" },
] as const;

const Navbar = ({ variant = "dark" }: TProps): React.ReactElement => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount, setCartOpen } = useCart();
  const isLight = variant === "light";
  const linkClassName = `4k:px-6 4k:py-4 font-sora 4k:text-lg rounded-2xl border px-[18px] py-[14px] text-base font-medium transition-colors ${
    isLight
      ? "border-primary/30 text-primary hover:bg-primary/10"
      : "border-white/30 text-white hover:bg-white/10"
  }`;

  return (
    <nav className="4k:px-16 4k:py-10 absolute top-0 right-0 left-0 z-50 px-4 py-5 sm:px-8 md:px-12 md:py-6">
      <div className="4k:max-w-none 4k:mx-0 mx-auto flex max-w-[1376px] items-center justify-between xl:max-w-none">
        <Link href="/" className="flex items-center">
          <Image
            src={
              isLight
                ? "/images/logos/simpleBLE-logo-dark.png"
                : "/images/logos/simpleBLE-logo.png"
            }
            alt="SimpleBLE"
            width={152}
            height={50}
            className="4k:h-16 h-10 w-auto sm:h-12"
          />
        </Link>

        <div className="4k:gap-5 hidden items-center gap-3 md:flex">
          {NAV_LINKS.map((item) => (
            <Link key={item.href} href={item.href} className={linkClassName}>
              {item.label}
            </Link>
          ))}
          <button
            type="button"
            onClick={() => setCartOpen(true)}
            className={`4k:px-6 4k:py-4 font-sora 4k:text-lg flex cursor-pointer items-center gap-2 rounded-2xl border px-[18px] py-[14px] text-base font-medium transition-colors ${
              isLight
                ? "border-primary/30 text-primary hover:bg-primary/10"
                : "border-white/30 text-white hover:bg-white/10"
            }`}
          >
            <ShoppingBag className="h-4 w-4" />
            Cart ({cartCount})
          </button>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={() => setCartOpen(true)}
            className={`relative flex h-11 w-11 cursor-pointer items-center justify-center rounded-full transition-colors ${
              isLight
                ? "text-primary hover:bg-primary/5"
                : "text-white hover:bg-white/10"
            }`}
            aria-label="Open cart"
          >
            <ShoppingBag className="h-5 w-5" />
            {cartCount > 0 && (
              <span className="absolute top-1.5 right-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold text-white">
                {cartCount}
              </span>
            )}
          </button>

          <button
            type="button"
            className={`flex h-11 w-11 cursor-pointer items-center justify-center ${
              isLight ? "text-primary" : "text-white"
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
            aria-controls="mobile-menu"
          >
            {isMobileMenuOpen ? (
              <X className="h-6 w-6" aria-hidden="true" />
            ) : (
              <Menu className="h-6 w-6" aria-hidden="true" />
            )}
          </button>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div id="mobile-menu" className="-mx-4 px-4 sm:-mx-8 sm:px-8 md:hidden">
          <div
            className={`mt-4 flex w-full flex-col gap-3 rounded-2xl p-4 ${
              isLight ? "bg-white shadow-lg" : "bg-primary/95 backdrop-blur-sm"
            }`}
          >
            {NAV_LINKS.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className={linkClassName}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <button
              type="button"
              onClick={() => {
                setIsMobileMenuOpen(false);
                setCartOpen(true);
              }}
              className={`font-sora flex cursor-pointer items-center justify-center gap-2 rounded-2xl px-[18px] py-[14px] text-base font-medium transition-colors ${
                isLight
                  ? "bg-primary hover:bg-primary/90 text-white"
                  : "text-primary bg-white hover:bg-white/90"
              }`}
            >
              <ShoppingBag className="h-4 w-4" />
              Cart ({cartCount})
            </button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
